import axios from 'axios';

// Create axios instance with base URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

console.log('API base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add timeout to prevent hanging requests
  timeout: 15000, // Increased timeout to 15 seconds
  // Enable cross-origin credentials
  withCredentials: false
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method.toUpperCase()} request to: ${config.baseURL}${config.url}`);
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Token attached to request');
    } else {
      console.warn('No authentication token found in localStorage');
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Response error:', error);
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error status:', error.response.status);
      console.error('Error data:', error.response.data);
      
      if (error.response.status === 401) {
        // Unauthorized, clear token and redirect to login
        console.error('Authentication failed - redirecting to login');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request setup error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await api.post('/api/users/token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });
    
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('token');
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/api/users/me');
    return response.data;
  },
};

// Vendor services
export const vendorService = {
  getAll: async () => {
    const response = await api.get('/api/vendors');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/vendors/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/api/vendors', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/api/vendors/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/vendors/${id}`);
    return response.data;
  },
  
  syncVendor: async (id) => {
    const response = await api.post(`/api/vendors/${id}/sync`);
    return response.data;
  },
};

// Product services
export const productService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/products', { params: filters });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/products/${id}`);
    return response.data;
  },
  
  getByVendorId: async (vendorId) => {
    // Ensure the path includes the trailing slash
    const response = await api.get(`/api/products/`, { params: { vendor_id: vendorId } });
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/api/products', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/api/products/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/products/${id}`);
    return response.data;
  },
  
  scrapeProductData: async (id) => {
    const response = await api.post(`/api/products/${id}/scrape`);
    return response.data;
  },
};

// Organization services
export const organizationService = {
  getMyOrganization: async () => {
    const response = await api.get('/api/organizations/me');
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/api/organizations/');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/organizations/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/organizations/', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/organizations/${id}`, data);
    return response.data;
  },

  getStats: async (id) => {
    const response = await api.get(`/api/organizations/${id}/stats`);
    return response.data;
  },

  getUsers: async (id) => {
    const response = await api.get(`/api/organizations/${id}/users`);
    return response.data;
  },
};

// Client Vendor services (organization's vendor relationships)
export const clientVendorService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/client-vendors/', { params: filters });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/client-vendors/${id}`);
    return response.data;
  },

  getByVendorId: async (vendorId) => {
    const response = await api.get(`/api/client-vendors/by-vendor/${vendorId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/client-vendors/', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/client-vendors/${id}`, data);
    return response.data;
  },

  remove: async (id) => {
    const response = await api.delete(`/api/client-vendors/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/client-vendors/stats');
    return response.data;
  },

  getExpiring: async (days = 90) => {
    const response = await api.get('/api/client-vendors/expiring', { params: { days } });
    return response.data;
  },

  getProducts: async (id) => {
    const response = await api.get(`/api/client-vendors/${id}/products`);
    return response.data;
  },

  getRecommendations: async (id) => {
    const response = await api.get(`/api/client-vendors/${id}/recommendations`);
    return response.data;
  },
};

// Analysis services
export const analysisService = {
  getDashboardData: async () => {
    try {
      console.log('Fetching dashboard data from:', `${API_BASE_URL}/api/analysis/dashboard`);
      const response = await api.get('/api/analysis/dashboard');
      console.log('Dashboard data response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Dashboard data error details:', error.response ? error.response.data : error.message);
      throw error;
    }
  },

  getSummary: async () => {
    const response = await api.get('/api/analysis/summary');
    return response.data;
  },

  analyzeVendors: async (mode = 'both', clientVendorIds = null, minSimilarity = 0.7) => {
    const params = { mode, min_similarity: minSimilarity };
    if (clientVendorIds) {
      params.client_vendor_ids = clientVendorIds;
    }
    const response = await api.post('/api/analysis/analyze', null, { params });
    return response.data;
  },

  getRecommendations: async (filters = {}) => {
    const response = await api.get('/api/analysis/recommendations', { params: filters });
    return response.data;
  },

  updateRecommendationStatus: async (id, data) => {
    const response = await api.put(`/api/analysis/recommendations/${id}`, data);
    return response.data;
  },

  getVendorOverlaps: async (minSimilarity = 0.75, minOverlapCount = 3, sameCategoryOnly = false, minCoreOverlaps = 3) => {
    const response = await api.get('/api/analysis/vendor-overlaps', {
      params: {
        min_similarity: minSimilarity,
        min_overlap_count: minOverlapCount,
        same_category_only: sameCategoryOnly,
        min_core_overlaps: minCoreOverlaps
      }
    });
    return response.data;
  },

  getVendorPairProducts: async (vendor1Id, vendor2Id, minSimilarity = 0.7) => {
    const response = await api.get(`/api/analysis/vendor-overlaps/${vendor1Id}/${vendor2Id}/products`, {
      params: { min_similarity: minSimilarity }
    });
    return response.data;
  },

  getConsolidation: async (vendor1Id, vendor2Id) => {
    const response = await api.get(`/api/analysis/consolidations/${vendor1Id}/${vendor2Id}`);
    return response.data;
  },

  updateConsolidation: async (vendor1Id, vendor2Id, data) => {
    const response = await api.put(`/api/analysis/consolidations/${vendor1Id}/${vendor2Id}`, data);
    return response.data;
  },

  getConsolidations: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/api/analysis/consolidations', { params });
    return response.data;
  },

  // Spend Analytics
  getSpendAnalytics: async (category = null, dateRange = null) => {
    const params = {};
    if (category) {
      params.category = category;
    }
    if (dateRange) {
      params.start_date = dateRange[0];
      params.end_date = dateRange[1];
    }
    const response = await api.get('/api/analysis/spend/analytics', { params });
    return response.data;
  },

  getVendorSpendDetail: async (vendorId, dateRange = null) => {
    const params = {};
    if (dateRange) {
      params.start_date = dateRange[0];
      params.end_date = dateRange[1];
    }
    const response = await api.get(`/api/analysis/spend/by-vendor/${vendorId}`, { params });
    return response.data;
  },

  getCategorySpendTrend: async (dateRange = null) => {
    const params = {};
    if (dateRange) {
      params.start_date = dateRange[0];
      params.end_date = dateRange[1];
    }
    const response = await api.get('/api/analysis/spend/category-trend', { params });
    return response.data;
  },
};

// Import services for spend file uploads
export const importService = {
  // Spend import endpoints
  previewSpendFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/imports/spend/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadSpendFile: async (file, columnMapping) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/imports/spend/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: {
        vendor_name_column: columnMapping.vendor_name,
        amount_column: columnMapping.amount,
        date_column: columnMapping.date,
        category_column: columnMapping.category || undefined,
      },
    });
    return response.data;
  },

  getSpendImports: async (skip = 0, limit = 50) => {
    const response = await api.get('/api/imports/spend', { params: { skip, limit } });
    return response.data;
  },

  getSpendImport: async (id) => {
    const response = await api.get(`/api/imports/spend/${id}`);
    return response.data;
  },

  deleteSpendImport: async (id, deleteRecords = false) => {
    const response = await api.delete(`/api/imports/spend/${id}`, {
      params: { delete_records: deleteRecords },
    });
    return response.data;
  },

  // Data cleansing endpoints
  detectDuplicates: async (threshold = 85, saveResults = false) => {
    const response = await api.post('/api/imports/cleansing/detect-duplicates', null, {
      params: { threshold, save_results: saveResults },
    });
    return response.data;
  },

  getDuplicateGroups: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/api/imports/cleansing/duplicate-groups', { params });
    return response.data;
  },

  mergeDuplicateGroup: async (groupId, canonicalVendorId, createAliases = true) => {
    const response = await api.post(`/api/imports/cleansing/duplicate-groups/${groupId}/merge`, null, {
      params: {
        canonical_vendor_id: canonicalVendorId,
        create_aliases: createAliases,
      },
    });
    return response.data;
  },

  updateDuplicateGroup: async (groupId, data) => {
    const response = await api.put(`/api/imports/cleansing/duplicate-groups/${groupId}`, data);
    return response.data;
  },

  // Supplier alias endpoints
  getAliases: async (vendorId = null) => {
    const params = vendorId ? { vendor_id: vendorId } : {};
    const response = await api.get('/api/imports/cleansing/aliases', { params });
    return response.data;
  },

  createAlias: async (data) => {
    const response = await api.post('/api/imports/cleansing/aliases', data);
    return response.data;
  },

  deleteAlias: async (aliasId) => {
    const response = await api.delete(`/api/imports/cleansing/aliases/${aliasId}`);
    return response.data;
  },

  // Category endpoints
  getCategoryDistribution: async () => {
    const response = await api.get('/api/imports/cleansing/categories');
    return response.data;
  },

  standardizeCategories: async (categoryMapping) => {
    const response = await api.post('/api/imports/cleansing/categories/standardize', categoryMapping);
    return response.data;
  },
};

// Contract repository services
export const contractService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/contracts/', { params: filters });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/contracts/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/contracts/stats');
    return response.data;
  },

  extractMetadata: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/contracts/extract-metadata', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  create: async (data, file = null) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    if (file) {
      formData.append('file', file);
    }
    const response = await api.post('/api/contracts/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/contracts/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/contracts/${id}`);
    return response.data;
  },

  uploadFile: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/contracts/${id}/upload-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getDownloadUrl: (id) => {
    const token = localStorage.getItem('token');
    return `${API_BASE_URL}/api/contracts/${id}/download?token=${token}`;
  },

  // Contract hierarchy (MSA/sub-contract structure)
  getHierarchy: async (filters = {}) => {
    const response = await api.get('/api/contracts/hierarchy', { params: filters });
    return response.data;
  },

  getSubContracts: async (parentId) => {
    const response = await api.get(`/api/contracts/${parentId}/sub-contracts`);
    return response.data;
  },

  getTopLevelContracts: async (filters = {}) => {
    const response = await api.get('/api/contracts/', {
      params: { ...filters, top_level_only: true }
    });
    return response.data;
  },
};

// Notification services
export const notificationService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/notifications/', { params });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/notifications/stats');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/notifications/${id}`);
    return response.data;
  },

  markAsRead: async (id) => {
    const response = await api.put(`/api/notifications/${id}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.put('/api/notifications/read-all');
    return response.data;
  },

  dismiss: async (id) => {
    const response = await api.put(`/api/notifications/${id}/dismiss`);
    return response.data;
  },

  generateAlerts: async () => {
    const response = await api.post('/api/notifications/generate');
    return response.data;
  },

  cleanup: async (daysOld = 90) => {
    const response = await api.delete('/api/notifications/cleanup', {
      params: { days_old: daysOld },
    });
    return response.data;
  },
};

// AI Chat services
export const chatService = {
  query: async (message, contextType = null) => {
    const response = await api.post('/api/chat/query', {
      message,
      context_type: contextType,
    });
    return response.data;
  },

  getSuggestions: async () => {
    const response = await api.get('/api/chat/suggestions');
    return response.data;
  },

  getCapabilities: async () => {
    const response = await api.get('/api/chat/capabilities');
    return response.data;
  },
};

// Vendor Scorecard services
export const scorecardService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/scorecards/', { params });
    return response.data;
  },

  getLatestForVendor: async (clientVendorId) => {
    const response = await api.get('/api/scorecards/', {
      params: { client_vendor_id: clientVendorId, limit: 1 }
    });
    return response.data.length > 0 ? response.data[0] : null;
  },

  getById: async (id) => {
    const response = await api.get(`/api/scorecards/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/scorecards/', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/scorecards/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/scorecards/${id}`);
    return response.data;
  },

  getRankings: async (params = {}) => {
    const response = await api.get('/api/scorecards/rankings', { params });
    return response.data;
  },

  getVendorTrend: async (clientVendorId, months = 12) => {
    const response = await api.get(`/api/scorecards/vendor/${clientVendorId}/trend`, {
      params: { months },
    });
    return response.data;
  },

  getWeights: async () => {
    const response = await api.get('/api/scorecards/weights/');
    return response.data;
  },

  updateWeights: async (data) => {
    const response = await api.put('/api/scorecards/weights/', data);
    return response.data;
  },
};

// Report services
export const reportService = {
  getTemplates: async () => {
    const response = await api.get('/api/reports/templates');
    return response.data;
  },

  preview: async (reportType, params = {}) => {
    const response = await api.get(`/api/reports/preview/${reportType}`, { params });
    return response.data;
  },

  download: async (reportType, format, params = {}) => {
    const response = await api.post(
      `/api/reports/generate/${reportType}`,
      { format, ...params },
      { responseType: 'blob' }
    );

    // Get filename from Content-Disposition header or generate one
    const contentDisposition = response.headers['content-disposition'];
    let filename = `${reportType}.${format === 'excel' ? 'xlsx' : format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename=(.+)/);
      if (match) {
        filename = match[1];
      }
    }

    // Trigger browser download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  },
};

// User management services
export const userService = {
  getAll: async (skip = 0, limit = 100) => {
    const response = await api.get('/api/users/', { params: { skip, limit } });
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/users/', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/users/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/users/${id}`);
    return response.data;
  },

  // Department memberships
  getDepartments: async (userId) => {
    const response = await api.get(`/api/users/${userId}/departments`);
    return response.data;
  },

  updateDepartments: async (userId, departmentIds) => {
    const response = await api.put(`/api/users/${userId}/departments`, departmentIds);
    return response.data;
  },
};

// Permission services
export const permissionService = {
  getMyPermissions: async () => {
    const response = await api.get('/api/permissions/my');
    return response.data;
  },

  getRoles: async () => {
    const response = await api.get('/api/permissions/roles');
    return response.data;
  },

  getResources: async () => {
    const response = await api.get('/api/permissions/resources');
    return response.data;
  },

  getActions: async () => {
    const response = await api.get('/api/permissions/actions');
    return response.data;
  },

  getRoleDefaults: async (role) => {
    const response = await api.get(`/api/permissions/defaults/${role}`);
    return response.data;
  },

  getPermissionMatrix: async () => {
    const response = await api.get('/api/permissions/matrix');
    return response.data;
  },

  getOverrides: async () => {
    const response = await api.get('/api/permissions/overrides');
    return response.data;
  },

  createOverride: async (data) => {
    const response = await api.post('/api/permissions/overrides', data);
    return response.data;
  },

  deleteOverride: async (id) => {
    const response = await api.delete(`/api/permissions/overrides/${id}`);
    return response.data;
  },

  checkPermission: async (resource, action) => {
    const response = await api.post('/api/permissions/check', null, {
      params: { resource, action },
    });
    return response.data;
  },
};

// Department services
export const departmentService = {
  getAll: async (includeInactive = false) => {
    const response = await api.get('/api/departments/', {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/departments/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/departments/', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/departments/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/departments/${id}`);
    return response.data;
  },

  addMember: async (departmentId, data) => {
    const response = await api.post(`/api/departments/${departmentId}/members`, data);
    return response.data;
  },

  removeMember: async (departmentId, userId) => {
    const response = await api.delete(`/api/departments/${departmentId}/members/${userId}`);
    return response.data;
  },

  setDepartmentHead: async (departmentId, userId, isHead = true) => {
    const response = await api.put(
      `/api/departments/${departmentId}/members/${userId}/head`,
      null,
      { params: { is_head: isHead } }
    );
    return response.data;
  },
};

// Approval services
export const approvalService = {
  // Workflows
  getWorkflows: async (includeInactive = false) => {
    const response = await api.get('/api/approvals/workflows', {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  createWorkflow: async (data) => {
    const response = await api.post('/api/approvals/workflows', data);
    return response.data;
  },

  updateWorkflow: async (id, data) => {
    const response = await api.put(`/api/approvals/workflows/${id}`, data);
    return response.data;
  },

  deleteWorkflow: async (id) => {
    const response = await api.delete(`/api/approvals/workflows/${id}`);
    return response.data;
  },

  // Requests
  getRequests: async (params = {}) => {
    const response = await api.get('/api/approvals/requests', { params });
    return response.data;
  },

  getPendingRequests: async () => {
    const response = await api.get('/api/approvals/requests/pending');
    return response.data;
  },

  getRequest: async (id) => {
    const response = await api.get(`/api/approvals/requests/${id}`);
    return response.data;
  },

  approve: async (id, notes = null) => {
    const response = await api.post(`/api/approvals/requests/${id}/approve`, { notes });
    return response.data;
  },

  reject: async (id, notes = null) => {
    const response = await api.post(`/api/approvals/requests/${id}/reject`, { notes });
    return response.data;
  },

  cancel: async (id) => {
    const response = await api.delete(`/api/approvals/requests/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/approvals/stats');
    return response.data;
  },
};

// MFA services
export const mfaService = {
  getStatus: async () => {
    const response = await api.get('/api/mfa/status');
    return response.data;
  },

  startEnrollment: async () => {
    const response = await api.post('/api/mfa/enroll/start');
    return response.data;
  },

  verifyEnrollment: async (code) => {
    const response = await api.post('/api/mfa/enroll/verify', { code });
    return response.data;
  },

  verifyCode: async (code) => {
    const response = await api.post('/api/mfa/verify', { code });
    return response.data;
  },

  verifyRecoveryCode: async (code) => {
    const response = await api.post('/api/mfa/recovery/verify', { code });
    return response.data;
  },

  regenerateRecoveryCodes: async (code) => {
    const response = await api.post('/api/mfa/recovery/regenerate', { code });
    return response.data;
  },

  disable: async (code) => {
    const response = await api.post('/api/mfa/disable', { code });
    return response.data;
  },

  // Admin
  getUserStatus: async (userId) => {
    const response = await api.get(`/api/mfa/admin/user/${userId}/status`);
    return response.data;
  },

  adminDisable: async (userId) => {
    const response = await api.delete(`/api/mfa/admin/user/${userId}`);
    return response.data;
  },
};

// Session services
export const sessionService = {
  getMySessions: async () => {
    const response = await api.get('/api/sessions/');
    return response.data;
  },

  revokeSession: async (sessionId) => {
    const response = await api.delete(`/api/sessions/${sessionId}`);
    return response.data;
  },

  revokeAllOther: async () => {
    const response = await api.delete('/api/sessions/');
    return response.data;
  },

  getCurrentSession: async () => {
    const response = await api.get('/api/sessions/current');
    return response.data;
  },

  // Admin
  getUserSessions: async (userId) => {
    const response = await api.get(`/api/sessions/admin/user/${userId}`);
    return response.data;
  },

  adminRevokeSession: async (sessionId) => {
    const response = await api.delete(`/api/sessions/admin/${sessionId}`);
    return response.data;
  },

  adminRevokeUserSessions: async (userId) => {
    const response = await api.delete(`/api/sessions/admin/user/${userId}`);
    return response.data;
  },
};

// SSO services
export const ssoService = {
  discover: async (email) => {
    const response = await api.post('/api/sso/discover', { email });
    return response.data;
  },

  getConfig: async () => {
    const response = await api.get('/api/sso/config');
    return response.data;
  },

  createConfig: async (data) => {
    const response = await api.post('/api/sso/config', data);
    return response.data;
  },

  updateConfig: async (data) => {
    const response = await api.put('/api/sso/config', data);
    return response.data;
  },

  deleteConfig: async () => {
    const response = await api.delete('/api/sso/config');
    return response.data;
  },

  testConnection: async () => {
    const response = await api.post('/api/sso/config/test');
    return response.data;
  },
};

// SCIM services
export const scimService = {
  getConfig: async () => {
    const response = await api.get('/api/scim/v2/admin/config');
    return response.data;
  },

  createConfig: async () => {
    const response = await api.post('/api/scim/v2/admin/config');
    return response.data;
  },

  updateConfig: async (data) => {
    const response = await api.put('/api/scim/v2/admin/config', data);
    return response.data;
  },

  deleteConfig: async () => {
    const response = await api.delete('/api/scim/v2/admin/config');
    return response.data;
  },

  regenerateToken: async () => {
    const response = await api.post('/api/scim/v2/admin/config/regenerate-token');
    return response.data;
  },
};

// Audit services
export const auditService = {
  getLogs: async (params = {}) => {
    const response = await api.get('/api/audit/', { params });
    return response.data;
  },

  getStats: async (days = 30) => {
    const response = await api.get('/api/audit/stats', { params: { days } });
    return response.data;
  },

  exportLogs: async (format = 'csv', params = {}) => {
    const response = await api.post('/api/audit/export', null, {
      params: { format, ...params },
      responseType: 'blob',
    });

    // Trigger download
    const filename = `audit-logs.${format}`;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  },

  getEventTypes: async () => {
    const response = await api.get('/api/audit/event-types');
    return response.data;
  },
};

// Phase 5.1: Purchase Order services
export const purchaseOrderService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/purchase-orders/', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/purchase-orders/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/purchase-orders/', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/purchase-orders/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/purchase-orders/${id}`);
    return response.data;
  },

  submit: async (id) => {
    const response = await api.post(`/api/purchase-orders/${id}/submit`);
    return response.data;
  },

  approve: async (id) => {
    const response = await api.post(`/api/purchase-orders/${id}/approve`);
    return response.data;
  },

  receive: async (id, receiptData) => {
    const response = await api.post(`/api/purchase-orders/${id}/receive`, receiptData);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/purchase-orders/stats');
    return response.data;
  },

  addLineItem: async (poId, data) => {
    const response = await api.post(`/api/purchase-orders/${poId}/line-items`, data);
    return response.data;
  },

  updateLineItem: async (poId, lineId, data) => {
    const response = await api.put(`/api/purchase-orders/${poId}/line-items/${lineId}`, data);
    return response.data;
  },

  deleteLineItem: async (poId, lineId) => {
    const response = await api.delete(`/api/purchase-orders/${poId}/line-items/${lineId}`);
    return response.data;
  },

  getReceipts: async (poId) => {
    const response = await api.get(`/api/purchase-orders/${poId}/receipts`);
    return response.data;
  },
};

// Phase 5.1: Invoice services
export const invoiceService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/invoices/', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/invoices/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/invoices/', data);
    return response.data;
  },

  upload: async (formData) => {
    const response = await api.post('/api/invoices/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  extractMetadata: async (formData) => {
    const response = await api.post('/api/invoices/extract-metadata', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/invoices/${id}`, data);
    return response.data;
  },

  downloadFile: async (id) => {
    const response = await api.get(`/api/invoices/${id}/file`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/invoices/stats');
    return response.data;
  },

  getAgingReport: async (asOfDate = null) => {
    const params = asOfDate ? { as_of_date: asOfDate } : {};
    const response = await api.get('/api/invoices/aging', { params });
    return response.data;
  },

  performMatch: async (id, tolerancePercentage = 2.0) => {
    const response = await api.post(`/api/invoices/${id}/match`, null, {
      params: { tolerance_percentage: tolerancePercentage },
    });
    return response.data;
  },

  autoMatch: async (id, tolerancePercentage = 2.0) => {
    const response = await api.post(`/api/invoices/${id}/auto-match`, null, {
      params: { tolerance_percentage: tolerancePercentage },
    });
    return response.data;
  },

  approve: async (id) => {
    const response = await api.post(`/api/invoices/${id}/approve`);
    return response.data;
  },

  reject: async (id, reason) => {
    const response = await api.post(`/api/invoices/${id}/reject`, null, {
      params: { reason },
    });
    return response.data;
  },

  recordPayment: async (id, paymentData) => {
    const response = await api.post(`/api/invoices/${id}/pay`, paymentData);
    return response.data;
  },

  getPayments: async (id) => {
    const response = await api.get(`/api/invoices/${id}/payments`);
    return response.data;
  },

  addLineItem: async (invoiceId, data) => {
    const response = await api.post(`/api/invoices/${invoiceId}/line-items`, data);
    return response.data;
  },

  deleteLineItem: async (invoiceId, lineId) => {
    const response = await api.delete(`/api/invoices/${invoiceId}/line-items/${lineId}`);
    return response.data;
  },
};

// Phase 5.3: Forecasting services
export const forecastingService = {
  getStats: async () => {
    const response = await api.get('/api/forecasting/stats');
    return response.data;
  },

  trainModel: async (data) => {
    const response = await api.post('/api/forecasting/train', data);
    return response.data;
  },

  getModels: async () => {
    const response = await api.get('/api/forecasting/models');
    return response.data;
  },

  deleteModel: async (modelId) => {
    const response = await api.delete(`/api/forecasting/models/${modelId}`);
    return response.data;
  },

  getAccuracy: async () => {
    const response = await api.get('/api/forecasting/accuracy');
    return response.data;
  },

  getForecasts: async (params) => {
    const response = await api.get('/api/forecasting/forecasts', { params });
    return response.data;
  },

  getStoredForecasts: async (params) => {
    const response = await api.get('/api/forecasting/forecasts/stored', { params });
    return response.data;
  },

  detectAnomalies: async (data) => {
    const response = await api.post('/api/forecasting/anomalies/detect', data);
    return response.data;
  },

  getAnomalies: async (params) => {
    const response = await api.get('/api/forecasting/anomalies', { params });
    return response.data;
  },

  getAnomaly: async (id) => {
    const response = await api.get(`/api/forecasting/anomalies/${id}`);
    return response.data;
  },

  resolveAnomaly: async (id, data) => {
    const response = await api.put(`/api/forecasting/anomalies/${id}/resolve`, data);
    return response.data;
  },

  getBudgetRecommendations: async (params) => {
    const response = await api.get('/api/forecasting/budget-recommendations', { params });
    return response.data;
  },

  getHistoricalSpend: async (params) => {
    const response = await api.get('/api/forecasting/historical', { params });
    return response.data;
  },
};

// Phase 5.4: Market Intelligence services
export const marketIntelligenceService = {
  // Statistics
  getStats: async () => {
    const response = await api.get('/api/market-intelligence/stats');
    return response.data;
  },

  // Risk Profiles
  getRiskProfiles: async (params) => {
    const response = await api.get('/api/market-intelligence/risk', { params });
    return response.data;
  },

  getVendorRisk: async (vendorId) => {
    const response = await api.get(`/api/market-intelligence/risk/${vendorId}`);
    return response.data;
  },

  getVendorRiskSummary: async (vendorId) => {
    const response = await api.get(`/api/market-intelligence/risk/${vendorId}/summary`);
    return response.data;
  },

  createRiskProfile: async (data) => {
    const response = await api.post('/api/market-intelligence/risk', data);
    return response.data;
  },

  updateRiskProfile: async (vendorId, data) => {
    const response = await api.put(`/api/market-intelligence/risk/${vendorId}`, data);
    return response.data;
  },

  // Market Pricing
  getVendorPricing: async (vendorId) => {
    const response = await api.get(`/api/market-intelligence/pricing/${vendorId}`);
    return response.data;
  },

  getCategoryPricing: async (category) => {
    const response = await api.get(`/api/market-intelligence/pricing/category/${encodeURIComponent(category)}`);
    return response.data;
  },

  createPricing: async (data) => {
    const response = await api.post('/api/market-intelligence/pricing', data);
    return response.data;
  },

  updatePricing: async (pricingId, data) => {
    const response = await api.put(`/api/market-intelligence/pricing/${pricingId}`, data);
    return response.data;
  },

  deletePricing: async (pricingId) => {
    const response = await api.delete(`/api/market-intelligence/pricing/${pricingId}`);
    return response.data;
  },

  // Price Benchmarking
  getBenchmark: async (params) => {
    const response = await api.get('/api/market-intelligence/benchmark', { params });
    return response.data;
  },

  runBenchmark: async (data) => {
    const response = await api.post('/api/market-intelligence/benchmark', data);
    return response.data;
  },

  // Data Sources
  getDataSources: async (enabledOnly = false) => {
    const response = await api.get('/api/market-intelligence/sources', {
      params: { enabled_only: enabledOnly },
    });
    return response.data;
  },

  getDataSource: async (sourceId) => {
    const response = await api.get(`/api/market-intelligence/sources/${sourceId}`);
    return response.data;
  },

  createDataSource: async (data) => {
    const response = await api.post('/api/market-intelligence/sources', data);
    return response.data;
  },

  updateDataSource: async (sourceId, data) => {
    const response = await api.put(`/api/market-intelligence/sources/${sourceId}`, data);
    return response.data;
  },

  deleteDataSource: async (sourceId) => {
    const response = await api.delete(`/api/market-intelligence/sources/${sourceId}`);
    return response.data;
  },

  // Data Refresh
  refreshVendorData: async (vendorId, params) => {
    const response = await api.post(`/api/market-intelligence/refresh/${vendorId}`, null, { params });
    return response.data;
  },
};

// Cost Center services
export const costCenterService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/cost-centers', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/cost-centers/${id}`);
    return response.data;
  },

  getTree: async () => {
    const response = await api.get('/api/cost-centers/tree');
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/cost-centers/stats');
    return response.data;
  },

  getDetailStats: async (id, year = null) => {
    const params = year ? { year } : {};
    const response = await api.get(`/api/cost-centers/${id}/stats`, { params });
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/cost-centers', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/cost-centers/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/cost-centers/${id}`);
    return response.data;
  },
};

// Task Management Service
export const taskService = {
  getStats: async () => {
    const response = await api.get('/api/tasks/stats');
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get('/api/tasks', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/tasks/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/tasks', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/tasks/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/tasks/${id}`);
    return response.data;
  },

  bulkUpdate: async (taskIds, updates) => {
    const response = await api.post('/api/tasks/bulk-update', null, {
      params: { task_ids: taskIds, ...updates }
    });
    return response.data;
  },

  // Comments
  getComments: async (taskId) => {
    const response = await api.get(`/api/tasks/${taskId}/comments`);
    return response.data;
  },

  addComment: async (taskId, content) => {
    const response = await api.post(`/api/tasks/${taskId}/comments`, { content });
    return response.data;
  },

  deleteComment: async (taskId, commentId) => {
    const response = await api.delete(`/api/tasks/${taskId}/comments/${commentId}`);
    return response.data;
  },

  // Create from entities
  createFromRecommendation: async (recommendationId, data = {}) => {
    const response = await api.post(`/api/tasks/from-recommendation/${recommendationId}`, null, {
      params: data
    });
    return response.data;
  },

  createFromContract: async (contractId, data = {}) => {
    const response = await api.post(`/api/tasks/from-contract/${contractId}`, null, {
      params: data
    });
    return response.data;
  },

  // Kanban board
  getKanbanBoard: async (params = {}) => {
    const response = await api.get('/api/tasks/board/kanban', { params });
    return response.data;
  },

  // Activity
  getRecentActivity: async (limit = 20) => {
    const response = await api.get('/api/tasks/recent/activity', { params: { limit } });
    return response.data;
  },
};

// Supplier Diversity Service
export const diversityService = {
  getCertificationTypes: async () => {
    const response = await api.get('/api/diversity/certification-types');
    return response.data;
  },

  getDashboard: async (fiscalYear = null) => {
    const params = fiscalYear ? { fiscal_year: fiscalYear } : {};
    const response = await api.get('/api/diversity/dashboard', { params });
    return response.data;
  },

  getTrend: async (months = 12) => {
    const response = await api.get('/api/diversity/trend', { params: { months } });
    return response.data;
  },

  // Certifications
  getCertifications: async (params = {}) => {
    const response = await api.get('/api/diversity/certifications', { params });
    return response.data;
  },

  getCertification: async (id) => {
    const response = await api.get(`/api/diversity/certifications/${id}`);
    return response.data;
  },

  createCertification: async (data) => {
    const response = await api.post('/api/diversity/certifications', data);
    return response.data;
  },

  updateCertification: async (id, data) => {
    const response = await api.put(`/api/diversity/certifications/${id}`, data);
    return response.data;
  },

  deleteCertification: async (id) => {
    const response = await api.delete(`/api/diversity/certifications/${id}`);
    return response.data;
  },

  verifyCertification: async (id, notes = null) => {
    const params = notes ? { verification_notes: notes } : {};
    const response = await api.put(`/api/diversity/certifications/${id}/verify`, null, { params });
    return response.data;
  },

  // Diverse Vendors
  getDiverseVendors: async (params = {}) => {
    const response = await api.get('/api/diversity/vendors', { params });
    return response.data;
  },

  // Goals
  getGoals: async (params = {}) => {
    const response = await api.get('/api/diversity/goals', { params });
    return response.data;
  },

  createGoal: async (data) => {
    const response = await api.post('/api/diversity/goals', data);
    return response.data;
  },

  updateGoal: async (id, data) => {
    const response = await api.put(`/api/diversity/goals/${id}`, data);
    return response.data;
  },

  deleteGoal: async (id) => {
    const response = await api.delete(`/api/diversity/goals/${id}`);
    return response.data;
  },

  // Spend calculation
  calculateSpend: async (params = {}) => {
    const response = await api.post('/api/diversity/calculate-spend', params);
    return response.data;
  },
};

// Vendor Mapping Service (Global Name Management)
export const vendorMappingService = {
  // Settings
  getSettings: async () => {
    const response = await api.get('/api/vendor-mapping/settings');
    return response.data;
  },

  updateSettings: async (data) => {
    const response = await api.put('/api/vendor-mapping/settings', data);
    return response.data;
  },

  // Matching
  findMatches: async (name, limit = 5, includeMlScores = true) => {
    const response = await api.post('/api/vendor-mapping/match', {
      name,
      limit,
      include_ml_scores: includeMlScores,
    });
    return response.data;
  },

  bulkMatch: async (names, minScore = 70) => {
    const response = await api.post('/api/vendor-mapping/match/bulk', {
      names,
      min_score: minScore,
    });
    return response.data;
  },

  // Pending Review Queue
  getPendingQueue: async (params = {}) => {
    const response = await api.get('/api/vendor-mapping/pending', { params });
    return response.data;
  },

  getPendingStats: async () => {
    const response = await api.get('/api/vendor-mapping/pending/stats');
    return response.data;
  },

  getPendingItem: async (id) => {
    const response = await api.get(`/api/vendor-mapping/pending/${id}`);
    return response.data;
  },

  approvePending: async (id, vendorId, createGlobalAlias = true, notes = null) => {
    const response = await api.post(`/api/vendor-mapping/pending/${id}/approve`, {
      vendor_id: vendorId,
      create_global_alias: createGlobalAlias,
      resolution_notes: notes,
    });
    return response.data;
  },

  rejectPending: async (id, reason = null) => {
    const response = await api.post(`/api/vendor-mapping/pending/${id}/reject`, {
      rejection_reason: reason,
    });
    return response.data;
  },

  createVendorFromPending: async (id, data) => {
    const response = await api.post(`/api/vendor-mapping/pending/${id}/create-vendor`, data);
    return response.data;
  },

  bulkApprovePending: async (pendingIds, createGlobalAliases = true) => {
    const response = await api.post('/api/vendor-mapping/pending/bulk/approve', {
      pending_ids: pendingIds,
      create_global_aliases: createGlobalAliases,
    });
    return response.data;
  },

  bulkRejectPending: async (pendingIds, reason = null) => {
    const response = await api.post('/api/vendor-mapping/pending/bulk/reject', {
      pending_ids: pendingIds,
      rejection_reason: reason,
    });
    return response.data;
  },

  refreshSuggestions: async (id) => {
    const response = await api.post(`/api/vendor-mapping/pending/${id}/refresh-suggestions`);
    return response.data;
  },

  autoResolvePending: async (threshold = 95, dryRun = true) => {
    const response = await api.post('/api/vendor-mapping/pending/auto-resolve', {
      threshold,
      dry_run: dryRun,
    });
    return response.data;
  },

  // Global Aliases
  getGlobalAliases: async (params = {}) => {
    const response = await api.get('/api/vendor-mapping/global-aliases', { params });
    return response.data;
  },

  getVendorAliases: async (vendorId) => {
    const response = await api.get(`/api/vendor-mapping/global-aliases/vendor/${vendorId}`);
    return response.data;
  },

  createGlobalAlias: async (data) => {
    const response = await api.post('/api/vendor-mapping/global-aliases', data);
    return response.data;
  },

  updateGlobalAlias: async (id, data) => {
    const response = await api.put(`/api/vendor-mapping/global-aliases/${id}`, data);
    return response.data;
  },

  deleteGlobalAlias: async (id) => {
    const response = await api.delete(`/api/vendor-mapping/global-aliases/${id}`);
    return response.data;
  },

  bulkUploadAliases: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/vendor-mapping/global-aliases/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Org Preferences
  getPreferences: async (params = {}) => {
    const response = await api.get('/api/vendor-mapping/preferences', { params });
    return response.data;
  },

  createPreference: async (data) => {
    const response = await api.post('/api/vendor-mapping/preferences', data);
    return response.data;
  },

  updatePreference: async (vendorId, data) => {
    const response = await api.put(`/api/vendor-mapping/preferences/${vendorId}`, data);
    return response.data;
  },

  deletePreference: async (vendorId) => {
    const response = await api.delete(`/api/vendor-mapping/preferences/${vendorId}`);
    return response.data;
  },

  // Name History
  getVendorNameHistory: async (vendorId) => {
    const response = await api.get(`/api/vendor-mapping/history/${vendorId}`);
    return response.data;
  },

  recordNameChange: async (vendorId, data) => {
    const response = await api.post(`/api/vendor-mapping/history/${vendorId}`, data);
    return response.data;
  },

  // ML Model
  getMLStats: async () => {
    const response = await api.get('/api/vendor-mapping/ml/stats');
    return response.data;
  },

  trainMLModel: async () => {
    const response = await api.post('/api/vendor-mapping/ml/train');
    return response.data;
  },

  // Suggestions
  suggestVendorsForInput: async (name, limit = 5) => {
    const response = await api.get(`/api/vendor-mapping/suggest/for-input/${encodeURIComponent(name)}`, {
      params: { limit },
    });
    return response.data;
  },

  suggestAliasesForVendor: async (vendorId) => {
    const response = await api.get(`/api/vendor-mapping/suggest/aliases/${vendorId}`);
    return response.data;
  },
};

// Savings Levers
export const savingsService = {
  getStats: async () => {
    const response = await api.get('/api/savings/stats');
    return response.data;
  },
  triggerScan: async (params = {}) => {
    const response = await api.post('/api/savings/scan', params);
    return response.data;
  },
  getOpportunities: async (params = {}) => {
    const response = await api.get('/api/savings/opportunities', { params });
    return response.data;
  },
  getOpportunity: async (id) => {
    const response = await api.get(`/api/savings/opportunities/${id}`);
    return response.data;
  },
  updateOpportunity: async (id, data) => {
    const response = await api.put(`/api/savings/opportunities/${id}`, data);
    return response.data;
  },
  getCategorySummary: async () => {
    const response = await api.get('/api/savings/summary-by-category');
    return response.data;
  },
};

// ============================================
// RFP Evaluation Service
// ============================================

export const rfpService = {
  // Projects
  createProject: async (data) => {
    const response = await api.post('/api/rfp/projects/', data);
    return response.data;
  },
  getProjects: async (params = {}) => {
    const response = await api.get('/api/rfp/projects/', { params });
    return response.data;
  },
  getProject: async (id) => {
    const response = await api.get(`/api/rfp/projects/${id}`);
    return response.data;
  },
  updateProject: async (id, data) => {
    const response = await api.put(`/api/rfp/projects/${id}`, data);
    return response.data;
  },
  deleteProject: async (id) => {
    const response = await api.delete(`/api/rfp/projects/${id}`);
    return response.data;
  },

  // BRD Upload & Extraction
  uploadBRD: async (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/rfp/projects/${projectId}/upload-brd`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
  extractRequirements: async (projectId) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/extract-requirements`, {}, {
      timeout: 120000,
    });
    return response.data;
  },
  getExtractionStatus: async (projectId) => {
    const response = await api.get(`/api/rfp/projects/${projectId}/extraction-status`);
    return response.data;
  },

  // Requirements
  getRequirements: async (projectId) => {
    const response = await api.get(`/api/rfp/projects/${projectId}/requirements/`);
    return response.data;
  },
  addRequirement: async (projectId, data) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/requirements/`, data);
    return response.data;
  },
  bulkAddRequirements: async (projectId, requirements) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/requirements/bulk`, { requirements });
    return response.data;
  },
  updateRequirement: async (reqId, data) => {
    const response = await api.put(`/api/rfp/requirements/${reqId}`, data);
    return response.data;
  },
  deleteRequirement: async (reqId) => {
    const response = await api.delete(`/api/rfp/requirements/${reqId}`);
    return response.data;
  },

  // Vendor Candidates
  getCandidates: async (projectId) => {
    const response = await api.get(`/api/rfp/projects/${projectId}/candidates/`);
    return response.data;
  },
  addCandidate: async (projectId, data) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/candidates/`, data);
    return response.data;
  },
  updateCandidate: async (candidateId, data) => {
    const response = await api.put(`/api/rfp/candidates/${candidateId}`, data);
    return response.data;
  },
  deleteCandidate: async (candidateId) => {
    const response = await api.delete(`/api/rfp/candidates/${candidateId}`);
    return response.data;
  },
  autoMatch: async (projectId) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/candidates/auto-match`, {}, {
      timeout: 120000,
    });
    return response.data;
  },

  getSuggestions: async (projectId, limit = 10) => {
    const response = await api.get(`/api/rfp/projects/${projectId}/candidates/suggestions`, {
      params: { limit },
      timeout: 120000,
    });
    return response.data;
  },

  // Scoring
  submitScore: async (projectId, data) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/scores/`, data);
    return response.data;
  },
  bulkSubmitScores: async (projectId, scores) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/scores/bulk`, { scores });
    return response.data;
  },
  updateScore: async (scoreId, data) => {
    const response = await api.put(`/api/rfp/scores/${scoreId}`, data);
    return response.data;
  },
  getScoringMatrix: async (projectId) => {
    const response = await api.get(`/api/rfp/projects/${projectId}/scoring-matrix`);
    return response.data;
  },
  getResults: async (projectId) => {
    const response = await api.get(`/api/rfp/projects/${projectId}/results`);
    return response.data;
  },
  exportCSV: (projectId) => {
    return `${API_BASE_URL}/api/rfp/projects/${projectId}/export`;
  },

  // Invitations
  createInvite: async (projectId, data) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/invites/`, data);
    return response.data;
  },
  getInvites: async (projectId) => {
    const response = await api.get(`/api/rfp/projects/${projectId}/invites/`);
    return response.data;
  },
  revokeInvite: async (inviteId) => {
    const response = await api.delete(`/api/rfp/invites/${inviteId}`);
    return response.data;
  },
  validateToken: async (token) => {
    const response = await api.get(`/api/rfp/invites/validate/${token}`);
    return response.data;
  },
  acceptInvite: async (token) => {
    const response = await api.post(`/api/rfp/invites/accept/${token}`);
    return response.data;
  },

  // Comments
  getComments: async (projectId, params = {}) => {
    const response = await api.get(`/api/rfp/projects/${projectId}/comments/`, { params });
    return response.data;
  },
  addComment: async (projectId, data) => {
    const response = await api.post(`/api/rfp/projects/${projectId}/comments/`, data);
    return response.data;
  },
  deleteComment: async (commentId) => {
    const response = await api.delete(`/api/rfp/comments/${commentId}`);
    return response.data;
  },
};

export default {
  auth: authService,
  vendors: vendorService,
  products: productService,
  organizations: organizationService,
  clientVendors: clientVendorService,
  analysis: analysisService,
  imports: importService,
  contracts: contractService,
  notifications: notificationService,
  chat: chatService,
  scorecards: scorecardService,
  reports: reportService,
  users: userService,
  permissions: permissionService,
  departments: departmentService,
  approvals: approvalService,
  mfa: mfaService,
  sessions: sessionService,
  sso: ssoService,
  scim: scimService,
  audit: auditService,
  purchaseOrders: purchaseOrderService,
  invoices: invoiceService,
  forecasting: forecastingService,
  marketIntelligence: marketIntelligenceService,
  costCenters: costCenterService,
  diversity: diversityService,
  tasks: taskService,
  vendorMapping: vendorMappingService,
  savings: savingsService,
  rfp: rfpService,
};